import React, { useState } from 'react';

import kebabCase from 'lodash/kebabCase';
import { Waypoint } from 'react-waypoint';

import { Button, Text } from '@change/ds-core-ui/next';
import { Checkbox } from '@change/ds-forms/next';

import { useTrackOnce } from 'src/app/shared/hooks/tracking';

import type { TaskShape } from '../../../../types';

import { activeTaskStyles } from './ActiveTask.styles';

type ActiveTaskProps = Omit<TaskShape, 'status'>;

export function ActiveTask({
	name,
	title,
	description,
	primaryActionText,
	primaryActionHref,
	primaryActionOnClick,
	onCheckboxChange,
	isFirstActive = false,
}: ActiveTaskProps): React.JSX.Element {
	const trackOnce = useTrackOnce();
	const [isChecked, setIsChecked] = useState(false);

	const handleCheckboxChange = (newValue: boolean) => {
		if (newValue && onCheckboxChange) {
			setIsChecked(true);
			void onCheckboxChange();
		}
	};

	const styles = activeTaskStyles();

	return (
		<div>
			<Waypoint onEnter={async () => trackOnce('petition_manage_tasks_view', { task: name })} />
			<div data-qa="petition-task" className="flex flex-col justify-between gap-4 py-5 md:flex-row md:gap-5">
				<div className={styles.checkboxWrapper()}>
					<Checkbox name={`task-checkbox-${kebabCase(name)}`} isReadOnly={!!isChecked} onChange={handleCheckboxChange}>
						<Text data-qa={`task-title-${kebabCase(name)}`} variant="body" className="text-size-body-bold" as="p">
							{title}
						</Text>
						<Text
							data-qa={`task-description-${kebabCase(name)}`}
							variant="body"
							size="small"
							className="text-typography-weak"
							as="p"
						>
							{description}
						</Text>
					</Checkbox>
				</div>
				<div className="flex flex-col items-start gap-6 md:flex-row md:items-center">
					{primaryActionHref ? (
						<Button
							as="a"
							variant={isFirstActive ? 'primary' : 'secondary'}
							size="x-small"
							href={primaryActionHref}
							onClick={primaryActionOnClick}
							data-qa={`task-action-button-${kebabCase(name)}`}
							className="bg-fill-inverse-strong w-full md:w-40"
						>
							{primaryActionText}
						</Button>
					) : (
						<Button
							variant={isFirstActive ? 'primary' : 'secondary'}
							size="x-small"
							onClick={primaryActionOnClick}
							data-qa={`task-action-button-${kebabCase(name)}`}
							className="bg-fill-inverse-strong w-full md:w-40"
						>
							{primaryActionText}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
